import { useState, useEffect, useCallback } from "react";
import ShopManagementList from "../components/organisms/ShopManagementList";
import ShopManagementForm from "../components/organisms/ShopManagementForm";
import {
  fetchShopManagement,
  createShop,
  updateShop,
  deactivateShop,
  deactivateRegi,
} from "../services/shopManagementService";
import { getCurrentPerson } from "../services/authService";

/**
 * ShopManagementPage - 店舗・レジ情報管理画面
 *
 * 一覧ビューとフォームビューをページ内で切り替える（React Router なし）。
 * 追加・編集は同一フォームコンポーネント（ShopManagementForm）を使用。
 */
function ShopManagementPage() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  // mode: 'list' | 'add' | 'edit'
  const [mode, setMode] = useState("list");
  const [editingShop, setEditingShop] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const loadShops = useCallback(async () => {
    try {
      const data = await fetchShopManagement();
      setShops(data);
    } catch (err) {
      console.error("店舗情報取得失敗:", err);
      setErrorMessage(`読み込みに失敗しました: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShops();
  }, [loadShops]);

  const handleAdd = () => {
    setEditingShop(null);
    setMode("add");
    setErrorMessage(null);
  };

  const handleEdit = (shop) => {
    setEditingShop(shop);
    setMode("edit");
    setErrorMessage(null);
  };

  const handleSubmit = async (payload) => {
    setErrorMessage(null);
    const person = getCurrentPerson();
    try {
      if (mode === "add") {
        await createShop({ ...payload, person });
      } else {
        await updateShop(editingShop.ShopNo, { ...payload, person });
      }
      await loadShops();
      setMode("list");
    } catch (err) {
      // UserID重複エラーはフォーム側でフィールドレベルの表示を行うため、ここでは汎用メッセージを出さない
      if (!err.conflictUserIds) {
        setErrorMessage(`保存に失敗しました: ${err.message}`);
      }
      throw err;
    }
  };

  const handleDeactivateShop = async (shopNo) => {
    setErrorMessage(null);
    try {
      await deactivateShop(shopNo);
      await loadShops();
      setMode("list");
    } catch (err) {
      setErrorMessage(`無効化に失敗しました: ${err.message}`);
      throw err;
    }
  };

  const handleDeactivateRegi = async (shopNo, regiNo) => {
    setErrorMessage(null);
    try {
      await deactivateRegi(shopNo, regiNo);
      // フォームのデータを再取得して最新状態に同期する
      const data = await fetchShopManagement();
      setShops(data);
      // editingShop も更新して、フォームが古いレジ一覧を表示し続けないようにする
      const updated = data.find((s) => s.ShopNo === shopNo);
      if (updated) setEditingShop(updated);
    } catch (err) {
      setErrorMessage(`無効化に失敗しました: ${err.message}`);
      throw err;
    }
  };

  const handleCancel = () => {
    setMode("list");
    setErrorMessage(null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-400 text-base">読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {errorMessage && (
        <p className="text-red-600 text-base mb-4">{errorMessage}</p>
      )}
      {mode === "list" ? (
        <ShopManagementList
          shops={shops}
          onAdd={handleAdd}
          onEdit={handleEdit}
        />
      ) : (
        <ShopManagementForm
          mode={mode}
          initialData={editingShop}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onDeactivateShop={handleDeactivateShop}
          onDeactivateRegi={handleDeactivateRegi}
        />
      )}
    </div>
  );
}

export default ShopManagementPage;
